import { useState } from "react";
import { Database, Cog, FileBarChart, Target, BarChart3, Plus, Minus } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import SBTIEmissionsChart from "./SBTIEmissionsChart";
import DataPipelineAnimation from "./DataPipelineAnimation";
import ScreenshotCarouselAnimation from "./ScreenshotCarouselAnimation";
import CarbonInitiativeTimeline from "./CarbonInitiativeTimeline";
import generateReportGif from "@/assets/generate-report.gif";

const DecarbonwizeHowItWorksSection = () => {
  const { t } = useLanguage();
  const [expandedStep, setExpandedStep] = useState<number>(0);
  const [carouselTab, setCarouselTab] = useState<number>(0);
  
  const carouselTabs = [
    { id: "overview", label: "Overview" },
    { id: "map", label: "Emissions Map" },
    { id: "flow", label: "Emissions Flow" },
  ];

  const steps = [
    {
      number: "01",
      icon: Database,
      title: t("howItWorks.step1Title"),
      description: t("howItWorks.step1Desc"),
      image: null,
      customComponent: "pipeline"
    },
    {
      number: "02",
      icon: Cog,
      title: t("howItWorks.step2Title"),
      description: t("howItWorks.step2Desc"),
      image: null,
      customComponent: "screenshots"
    },
    {
      number: "03",
      icon: FileBarChart,
      title: t("howItWorks.step3Title"),
      description: t("howItWorks.step3Desc"),
      image: generateReportGif
    },
    {
      number: "04",
      icon: Target,
      title: t("howItWorks.step4Title"),
      description: t("howItWorks.step4Desc"),
      image: null,
      customComponent: "chart"
    },
    {
      number: "05",
      icon: BarChart3,
      title: t("howItWorks.step5Title"),
      description: t("howItWorks.step5Desc"),
      image: null,
      customComponent: "timeline"
    }
  ];

  const handleStepClick = (index: number) => {
    setExpandedStep(index);
  };

  return (
    <section id="how-it-works" className="py-24 lg:py-32 px-2">
      <div className="bg-[#3a3631] rounded-[31px] relative overflow-hidden py-16 lg:py-24 px-6 sm:px-8 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }} />
          </div>

          <div className="relative z-10">
            {/* Centered Section Header */}
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                {t("howItWorks.title1")}
                <span className="text-[#166a9c]"> {t("howItWorks.title2")}</span>
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                {t("howItWorks.description")}
              </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Left Column - Accordion */}
              <div>
                <div className="space-y-0">
                  {steps.map((step, index) => (
                    <div key={step.number} className="border-t border-white/10 last:border-b">
                      <button
                        onClick={() => handleStepClick(index)}
                        className="w-full flex items-center justify-between py-5 text-left group transition-colors duration-300 hover:bg-white/5"
                      >
                        <div className="flex items-center gap-4">
                          <span className={`text-lg font-bold transition-colors duration-300 ${
                            expandedStep === index ? 'text-[#166a9c]' : 'text-white/40'
                          }`}>
                            {step.number}
                          </span>
                          <h3 className={`text-lg lg:text-xl font-medium transition-colors duration-300 ${
                            expandedStep === index ? 'text-white' : 'text-white/70 group-hover:text-white'
                          }`}>
                            {step.title}
                          </h3>
                        </div>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                          expandedStep === index 
                            ? 'text-[#166a9c]' 
                            : 'text-[#166a9c] group-hover:bg-[#166a9c]/10'
                        }`}>
                          {expandedStep === index ? (
                            <Minus className="w-4 h-4" />
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                        </div>
                      </button>
                      
                      {/* Expanded Content */}
                      <div className={`overflow-hidden transition-all duration-500 ease-out ${
                        expandedStep === index ? 'max-h-40 opacity-100 pb-5' : 'max-h-0 opacity-0'
                      }`}>
                        <p className="text-white/60 leading-relaxed pl-10">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column - Image Display */}
              <div className="relative lg:sticky lg:top-24">
                {/* Glow Effect */}
                <div className="absolute -inset-4 bg-gradient-to-br from-[#166a9c]/20 via-[#166a9c]/5 to-transparent rounded-3xl blur-2xl opacity-60" />
                
                {/* Image Container */}
                <div className={`relative overflow-hidden ${
                  expandedStep === 1 || expandedStep === 2 ? 'aspect-[16/9]' : 'aspect-[16/10] rounded-2xl'
                }`}>
                  {expandedStep >= 0 && expandedStep < steps.length && (
                    steps[expandedStep].customComponent === "chart" ? (
                      <div key={expandedStep} className="w-full h-full animate-fade-in">
                        <SBTIEmissionsChart />
                      </div>
                    ) : steps[expandedStep].customComponent === "pipeline" ? (
                      <div key={expandedStep} className="w-full h-full animate-fade-in">
                        <DataPipelineAnimation />
                      </div>
                    ) : steps[expandedStep].customComponent === "screenshots" ? (
                      <div key={expandedStep} className="w-full h-full animate-fade-in">
                        <ScreenshotCarouselAnimation onTabChange={setCarouselTab} />
                      </div>
                    ) : steps[expandedStep].customComponent === "timeline" ? (
                      <div key={expandedStep} className="w-full h-full animate-fade-in">
                        <CarbonInitiativeTimeline />
                      </div>
                    ) : (
                      <img 
                        key={expandedStep}
                        src={steps[expandedStep].image}
                        alt={steps[expandedStep].title}
                        loading="eager"
                        className="w-full h-full object-cover animate-fade-in"
                      />
                    )
                  )}
                  
                  {/* Overlay with step info */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#3a3631]/80 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 pointer-events-none">
                    {expandedStep >= 0 && expandedStep < steps.length && (
                      <div className="animate-fade-in">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#166a9c]/20 border border-[#166a9c]/30 mb-3">
                          <span className="text-xs font-medium text-[#166a9c]">Step {steps[expandedStep].number}</span>
                        </span>
                        <h4 className="text-xl font-bold text-white">
                          {steps[expandedStep].title}
                        </h4>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Standards alignment - shown below image for Generate Reports step */}
                {expandedStep === 2 && (
                  <div className="mt-4 flex flex-col items-center gap-2 animate-fade-in">
                    <span className="text-white/60 text-sm font-medium">{t("howItWorks.standardsAlignment")}</span>
                    <img 
                      src="/lovable-uploads/8d56f73e-d357-4ec7-bd6a-9832088bff37.png"
                      alt="Greenhouse Gas Protocol"
                      className="h-12 object-contain"
                    />
                  </div>
                )}
                
                {/* Dot Indicators for screenshots - below the image */}
                {expandedStep === 1 && (
                  <div className="flex gap-3 justify-center mt-4">
                    {carouselTabs.map((tab, index) => (
                      <button
                        key={tab.id}
                        onClick={() => setCarouselTab(index)}
                        aria-label={tab.label}
                        className={`h-3 rounded-full transition-all duration-300 ${
                          carouselTab === index
                            ? "bg-[#166a9c] w-8"
                            : "bg-white/40 w-3 hover:bg-white/60"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DecarbonwizeHowItWorksSection;
